<template>
  <div>
    <v-sheet rounded outlined>
      <TableSkeleton
        v-if="isLoading"
        data-testid="explore-tableSkeleton"
        :columns="selectedHeaders.length"
        :rows="tableOptions.itemsPerPage"
      />
      <v-data-table
        v-show="!isLoading && totalItems > 0"
        calculate-widths
        data-private
        data-testid="explore-tableView"
        :headers="selectedHeaders"
        :footer-props="footerOptions"
        :items="rows"
        :loading="isLoading"
        :options.sync="currentTableOptions"
        :server-items-length="totalItems"
        :mobile-breakpoint="$vuetify.breakpoint.thresholds.md"
        @click:row="$emit('click', $event)"
      >
        <template v-slot:[`item.name`]="{ item }">
          <span
            v-if="item.entityType === $constants.exploreEntityTypes.finding"
            @click="$emit('openFinding', item.id)"
          >
            <text-highlight
              highlightClass="yellow lighten-1"
              class="body2 link-td"
              highlightComponent="span"
              :queries="searchValue"
              >{{ item.name }}</text-highlight
            >
          </span>

          <span
            v-else-if="item.entityType === $constants.exploreEntityTypes.task"
            @click="openTaskDrawer(item.id)"
          >
            <text-highlight
              highlightClass="yellow lighten-1"
              class="body2 link-td"
              highlightComponent="span"
              :queries="searchValue"
              >{{ item.name }}</text-highlight
            >
          </span>

          <router-link v-else class="router-link" :to="getRouteForEntity(item)">
            <text-highlight
              highlightClass="yellow lighten-1"
              class="body2 link-td"
              highlightComponent="span"
              :queries="searchValue"
              >{{ item.name }}</text-highlight
            >
          </router-link>
          <v-tooltip v-if="item.shouldShowHelper" top max-width="250px">
            <template v-slot:activator="{ on }">
              <v-icon class="ml-2" color="yellow" size="16px" v-on="on"
                >mdi-alert-circle-outline
              </v-icon>
            </template>
            <p class="body2 mb-0">
              {{ $t('common.users.result_based_on_matches_by_fields') }}
            </p>
            <ul>
              <li v-for="label in item.matchingFieldsLabels" :key="label">
                {{ label }}
              </li>
            </ul>
          </v-tooltip>
        </template>
        <template v-slot:[`item.type`]="{ item }">
          <EntityChip icon :entityType="item.entityType" />
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <StatusChip showValidationIcons :entity="item" />
        </template>
        <template v-slot:[`item.createdBy`]="{ item }">
          <ChipAvatar small :account="item.createdBy" />
        </template>
        <template v-slot:[`item.coach`]="{ item }">
          <ChipAvatar small :account="item.coach" />
        </template>
        <template v-slot:[`item.tags`]="{ item }">
          <ChipList outlined :items="item.tags" />
        </template>
        <template v-slot:[`item.totalLikes`]="{ item }">
          <span
            class="body2 blue-grey--text text--darken-3"
            :data-testid="`exploreContentTable-totalLikes-${item.id}`"
          >
            {{ item.totalLikes !== undefined ? item.totalLikes : '-' }}
          </span>
        </template>
        <template v-slot:[`item.routine`]="{ item }">
          <a
            v-if="hasRoutineLink(item)"
            class="explore-content-table__routine-link"
            data-testid="explore-content-table-routineLink"
            :href="getRoutineExecutionLink(item)"
            rel="noopener noreferrer"
            target="_blank"
          >
            <span
              class="explore-content-table__routine-link-text"
              :data-testid="`explore-routineLink-${item.id}`"
              :title="item.routine.name"
            >
              {{ item.routine.name }}
            </span>
          </a>
          <span
            v-else
            class="body2 blue-grey--text text--darken-3"
            :data-testid="`explore-routineFallback-${item.id}`"
          >
            -
          </span>
        </template>
        <template v-slot:[`item.assignees`]="{ item }">
          <div class="text-no-wrap">
            <ChipAvatar
              v-if="item.assignees.length"
              small
              :account="item.firstAssignee"
            />
            <span
              v-if="item.assignees.length > 1"
              class="caption blue-grey--text text--darken-3"
            >
              +{{ item.assignees.length - 1 }}
            </span>
          </div>
        </template>
        <template v-slot:[`item.createdAt`]="{ item }">
          <span class="body2 blue-grey--text text--darken-3">
            {{ item.createdDate }}
          </span>
        </template>
        <template v-slot:[`item.updatedAt`]="{ item }">
          <span class="body2 blue-grey--text text--darken-3">
            {{ item.modifiedDate }}
          </span>
        </template>
        <template v-slot:[`item.predictedFinancialImpactInUSD`]="{ item }">
          <span
            class="body2 blue-grey--text text--darken-3"
            :data-testid="`exploreContentTable-predictedFinancialImpact-${item.id}`"
          >
            {{ formatToUserCurrency(item.predictedFinancialImpactInUSD) }}
          </span>
        </template>
      </v-data-table>
    </v-sheet>
  </div>
</template>

<script>
import { utils } from 'frontend-js-shared'
import { mapGetters } from 'vuex'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

import EntityChip from '@/components/EntityChip.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import ChipList from '@/components/ChipList.vue'
import TableSkeleton from '@/components/TableSkeleton.vue'

import StatusChip from '@/components/Core/StatusChip.vue'

import moneyMixin from '@/mixins/moneyMixin.vue'

export default {
  name: 'ExploreContentTable',

  mixins: [moneyMixin],

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    tableOptions: {
      type: Object,
      default: () => ({}),
    },
    results: {
      type: Array,
      default: () => [],
    },
    searchValue: {
      type: String,
      default: '',
    },

    selectedHeaders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    rows() {
      return this.results.map(item => {
        const result = item.response
        return {
          ...result,
          name: result.name || result.situation,
          shouldShowHelper: this.shouldShowHelper(item.matchingFields),
          matchingFieldsLabels: this.getMatchingFieldLabels(
            item.matchingFields
          ),
          createdBy: this.getOwner(result),
          assignees: result.assignees || [],
          firstAssignee: result.assignees?.[0],
          tags: this.getParsedTags(result?.tags),
          createdDate: result.createdAt
            ? utils.dates.formatDate(result.createdAt, 'MMM D, YYYY, h:mm A')
            : '',
          modifiedDate: result.updatedAt
            ? utils.dates.formatDate(result.updatedAt, 'MMM D, YYYY, h:mm A')
            : '',
        }
      })
    },

    currentTableOptions: {
      get() {
        const sort = this.$route.query.sort ?? 'createdAt-desc'
        return {
          page: this.tableOptions.page,
          itemsPerPage: this.tableOptions.itemsPerPage,
          sortBy: [sort.split('-')?.[0]] ?? [],
          sortDesc: [sort.split('-')?.[1] === 'desc'] ?? [],
        }
      },
      set(event) {
        const pageChanged = event.page !== this.currentTableOptions.page
        const itemsPerPageChanged =
          event.itemsPerPage !== this.currentTableOptions.itemsPerPage
        const sortByChanged =
          event?.sortBy?.[0] !== this.currentTableOptions?.sortBy?.[0]
        const sortDescChanged =
          event?.sortDesc?.[0] !== this.currentTableOptions?.sortDesc?.[0]
        if (
          pageChanged ||
          itemsPerPageChanged ||
          sortByChanged ||
          sortDescChanged
        ) {
          const sortBy = event?.sortBy?.[0]
          const sortOrder = event?.sortDesc?.[0] ? 'desc' : 'asc'
          if (sortBy && sortOrder) {
            this.$emit('sort', `${sortBy}-${sortOrder}`)
          } else {
            this.$emit('sort', '')
          }

          this.$emit('changePage', event.page)
          this.$emit('changeItems', event.itemsPerPage)
        }
      },
    },
  },

  methods: {
    getOwner(data) {
      return data?.owner ?? data.createdBy ?? {}
    },
    getParsedTags(tags) {
      return tags?.map?.(tag => tag?.name) ?? []
    },

    getMatchingFieldLabels(fields = []) {
      return fields.map(field =>
        this.$t(`explore.fields.${field.replace('.', '_')}`)
      )
    },
    shouldShowHelper(fields = []) {
      if (!fields.length) return false
      return !fields.some(field => ['name', 'situation'].includes(field))
    },

    hasRoutineLink(item = {}) {
      const isFinding =
        item.entityType === this.$constants.exploreEntityTypes.finding

      return isFinding && Boolean(item.routine?.id)
    },

    getRoutineExecutionLink(item = {}) {
      return `/routine-executions/${item.routine?.id}`
    },

    getRouteForEntity(item) {
      const { entityType, id, organization } = item
      switch (entityType) {
        case this.$constants.exploreEntityTypes.improvementIdea:
          return `/rev/${organization}/${id}`
        case this.$constants.exploreEntityTypes.problemSolving:
          return `/problem-solving/${organization}/${id}`
        case this.$constants.exploreEntityTypes.quickAction:
          return `/quick-action/${organization}/${id}`
        case this.$constants.exploreEntityTypes.routine:
          return `/routine-executions/${id}`
        case this.$constants.exploreEntityTypes.challenge:
          return `/challenge/${organization}/${id}`
        default:
          return ''
      }
    },

    openTaskDrawer(taskId) {
      eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE, taskId)
    },
  },

  components: {
    EntityChip,
    ChipAvatar,
    StatusChip,
    ChipList,
    TableSkeleton,
  },
}
</script>
<style lang="scss" scoped>
.v-application a.router-link {
  color: $color-primary;
  text-decoration: underline;
}

.explore-content-table {
  &__routine-link {
    color: $color-primary;
    display: inline-block;
    max-width: 160px;
    text-decoration: underline;
  }

  &__routine-link-text {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
